import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Tout ce qui éclaire est un néon.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'lightcycles',
  family: 'geometric',
  danceability: 0.90,
  energyLevel: 0.86,
  bpmMin: 115,
  bpmMax: 175,
  movement: 0.85,
  tonality: 0.40,
  prefers: { gridStrength: [0.65, 1.0] },
  // the wall segments live in a float texture read by the vertex shader
  enabled: FLOAT_TEXTURES_OK
};

// LIGHTCYCLES : sur une grille noire infinie, des motos de lumière filent en
// ligne droite et laissent des murs de néon ; elles tournent à angle droit
// sur les temps, s'évitent, se coincent ; au drop elles s'écrasent ensemble
// en gerbes d'étincelles. Techno, synthwave, électro, drum'n'bass. Règles de
// kiosk-app/CLAUDE.md (R1 à R19), preset « party » de vdjLook.
//
// TOUT EST DISCRET. Les motos sont sur des CASES (entiers x, z), avancent
// d'une case par croche (par double croche en montée), ne tournent que d'un
// quart de tour, et seulement sur un temps. Le PILOTAGE est calculé côté CPU
// une fois par temps : pour chaque moto, tout droit / gauche / droite, avec
// pour score l'espace libre atteignable depuis les cases qu'elle va
// parcourir pendant ce temps (parcours en largeur limité à 8 cases) ; les
// cases parcourues sont RÉSERVÉES au fur et à mesure, d'une moto à
// l'autre : aucune ne peut entrer dans un mur ni dans la case d'une autre.
// Préférence pour les virages sur les temps forts. Sans issue, une moto
// s'écrase seule et réapparaît au bord de la zone.
//
// LES MURS (R16 : l'historique réel). Chaque moto garde la file des cases
// qu'elle a parcourues (64 au plus, 32 au tier 0) ; la file est compressée
// en SEGMENTS (un par ligne droite) et écrite une fois par temps dans une
// texture flottante (64 x 2 lignes par moto : extrémités, puis odomètre
// des deux bouts). Le segment en cours, de son dernier virage à la moto,
// prend sa fin dans un uniform (la tête, interpolée à chaque image) : rien
// d'autre ne bouge entre deux temps. Un mur est un ruban vertical instancié,
// bas (0,35 case), dessiné par distance en pixels (R1) : l'ARÊTE supérieure
// est l'élément principal (cœur blanc 2 px, gaine de la couleur), la face
// presque transparente (18 % de la couleur, plus claire vers l'arête),
// additif ; il s'éteint depuis la QUEUE selon
// l'odomètre (la distance à la tête). Une seconde instance, à plat, fait la
// lueur de chaque mur au sol, 0,5 case de chaque côté à 20 % (nulle avant le
// bord de son quad, R12).
//
// LA GRILLE : un plan noir, des lignes de 1 px par distance (fwidth) à
// luminance 0,25 ; quand une case passe sous ~3 px, la ligne se fond dans sa
// couverture moyenne (aucun moiré), puis s'éteint. L'ARÈNE fait 48 x 32
// cases, centrée sur l'origine ; son bord est un mur infranchissable (les
// motos roulent sur les points intérieurs) dessiné en liseré néon discret
// dans le shader de la grille ; dehors, la grille s'atténue.
//
// LA CAMÉRA montre TOUJOURS l'arène entière (R6, R19) : pour chaque vue et
// chaque format d'écran, un petit solveur cherche la distance et le recul
// qui font tenir les huit coins de l'arène (murs compris) entre 10 % du bas
// et 12 % du haut (zones d'interface), 5 % sur les côtés, centrés sur
// l'écran. Trois vues en coupe sur la phrase : trois quarts haute à 55°,
// dessus pure, trois quarts de l'autre côté. Le rase-mottes derrière une
// moto est réservé au drop, pendant 2 mesures.
//
// LES MOTOS : 6, toujours en jeu (une moto coincée réapparaît au bord et
// repart dans le même temps). Corps en goutte effilée vers l'avant, bas,
// laqué noir (matcap généré dans le shader, R5 : deux reflets larges sous
// le seuil, Fresnel de sa couleur), arête latérale de sa couleur à cœur
// blanc, deux roues en anneaux lumineux ; 1,5 case de long. Phare : un cône
// de lumière au sol devant elle. Dans un virage, elle pivote pendant le pas
// et s'incline de 15° vers l'intérieur, puis se redresse. Le mur finit au
// centre de la moto, caché dans son corps opaque : aucun espace.
//
// LE RYTHME : CROCHE une case ; TEMPS virages, arêtes +10 % ; MESURE une moto
// change de couleur en passant par le blanc (elle échange sa teinte avec une
// autre), à-coup de caméra ; PHRASE vue suivante ; MONTÉE une case par
// double croche (murs toujours de 64 cases) ; DROP collision au centre : gerbes
// d'étincelles (points balistiques, R7), murs brisés qui s'éteignent en
// 2 temps, flash, puis départ en étoile sur une grille vide.
//
// COULEURS (R4) : les teintes des motos sont réparties à 1/n du tour à
// partir de la teinte d'accent de la pochette : 0,25 avec 4 motos ; avec 5
// ou 6, l'écart de 0,25 entre toutes est géométriquement impossible (0,20
// et 0,167).

// six bikes at every tier (the scene is unreadable with fewer); tier 0 keeps
// half the segment slots
const TIER = { 0: { bikes: 6, segs: 32 }, 1: { bikes: 6, segs: 64 }, 2: { bikes: 6, segs: 64 } };
const MAX_SEGS = 64;
const WALL_LEN = 64, WALL_LEN_BUILD = 64;   // cells of wall per bike (always 64)
const FADE_CELLS = 14;                      // the tail fades over the last cells
const WALL_H = 0.35;
// the ribbon runs a little ABOVE its top edge, so the edge's core and sheath
// fall off inside the quad: an edge drawn on the quad's own border is a hard
// staircase, and the bloom turns every step into a white puff
const EDGE_OVER = 1.3;
// the arena: 48 x 32 cells between the border lines x = +-24, z = +-16; the
// bikes ride the interior points |x| <= 23, |z| <= 15
const BORDER = { x: 24, z: 16 };
const ARENA = { x: BORDER.x - 1, z: BORDER.z - 1 };
const GW = 2 * ARENA.x + 1, GH = 2 * ARENA.z + 1;
const LOOK = 8;                             // the free-space search depth (cells)
const LOOK_CAP = 60;                        // pocket test: this many free cells reachable = open
const SPARKS = 4096, BURSTS = 8;
const DIRS = [[1, 0], [0, 1], [-1, 0], [0, -1]];
const VIEWS = ['high', 'top', 'high2'];          // the phrase cuts
const PIN_VIEWS = ['high', 'top', 'high2', 'low'];
const FOV = 50;
const PITCH = 55;                                // degrees, the three-quarter views
// the framing box in NDC: 10 % off the bottom, 12 % off the top (the
// interface zones of R6), 5 % off each side
const FRAME = { yb: -0.80, yt: 0.76, x: 0.90 };
const LEAN = THREE.MathUtils.degToRad(15);
const BODY_Y = 0.30;                             // the bike body's axis height
const LOW_BEATS = 8;                             // the drop's low shot: 2 bars
const GRID_LUM = 0.25;
const SAFE_FLOOR = 0.5;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const FALLBACK = [0xff2bd6];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function rng(seed) { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// ----------------------------------------------------------------- shaders

const SAFE_CHUNK = `
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const GRID_VERTEX = `
  varying vec3 vW;
  varying vec2 vNdc;
  void main() {
    vec4 w = modelMatrix * vec4(position, 1.0);
    vW = w.xyz;
    vec4 c = projectionMatrix * viewMatrix * w;
    vNdc = c.xy / c.w;
    gl_Position = c;
  }
`;
const GRID_FRAGMENT = `
  precision highp float;
  uniform vec3 uCol;
  uniform float uGain;
  varying vec3 vW;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  // a 1 px line at the integers of c; as a cell shrinks under ~4 px it
  // melts into its mean coverage (no moire), then fades to black before that
  // coverage piles up into a grey band at the horizon (R3)
  float gridLine(float c) {
    float w = max(fwidth(c), 1e-5);
    float d = abs(fract(c + 0.5) - 0.5) / w;
    float line = 1.0 - smoothstep(0.0, 1.0, d);
    return mix(line, w, smoothstep(0.22, 0.45, w)) * (1.0 - smoothstep(0.30, 0.60, w));
  }
  void main() {
    float g = max(gridLine(vW.x), gridLine(vW.z));
    // the arena's border: signed distance to the rectangle, in pixels (R1)
    vec2 q = abs(vW.xz) - vec2(${BORDER.x}.0, ${BORDER.z}.0);
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float px = abs(sd) / max(fwidth(sd), 1e-5);
    float border = (1.0 - smoothstep(0.5, 1.5, px)) * 0.30 + exp(-px / 5.0) * 0.04;
    // outside the arena the grid dims, then dies away
    float outside = max(sd, 0.0);
    float fade = mix(1.0, 0.45, step(0.0, sd)) * exp(-outside / 10.0);
    gl_FragColor = vec4(uCol * (g * ${GRID_LUM.toFixed(2)} * fade + border) * uGain * safeAtt(vNdc), 1.0);
  }
`;

// the walls: one instance per (bike, segment); position.x along the segment
// (0..1), position.y up (0..1) for the ribbon, across (-1..1) for the glow
function wallVertex(flat) {
  return `
  attribute float aBike;
  attribute float aSeg;
  uniform sampler2D uSegs;
  uniform vec2 uSegsSize;        // texture width (segments), height (rows)
  uniform vec4 uHead[6];         // head x, z, odometer, -
  uniform float uHeadOdo[6];
  uniform float uLen;            // wall length (cells)
  uniform float uBreak;          // the drop: walls shattering, 0..1
  varying vec2 vUv;
  varying float vFade;
  varying float vBike;
  varying vec2 vNdc;
  float h11(float n) { return fract(sin(n * 127.1 + 311.7) * 43758.5453); }
  void main() {
    vec4 e = texture2D(uSegs, vec2((aSeg + 0.5) / uSegsSize.x, (aBike * 2.0 + 0.5) / uSegsSize.y));
    vec4 o = texture2D(uSegs, vec2((aSeg + 0.5) / uSegsSize.x, (aBike * 2.0 + 1.5) / uSegsSize.y));
    vFade = 0.0; vBike = aBike;
    if (o.z < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    int b = int(aBike + 0.5);
    vec4 hd = uHead[0]; float hodo = uHeadOdo[0];
    for (int k = 1; k < 6; k++) if (k == b) { hd = uHead[k]; hodo = uHeadOdo[k]; }
    vec2 a = e.xy, c = e.zw; float o0 = o.x, o1 = o.y;
    if (o.z > 1.5) { c = hd.xy; o1 = hodo; }          // the live segment ends at the head
    vec2 p = mix(a, c, position.x);
    float odo = mix(o0, o1, position.x);
    float back = hodo - odo;                           // cells behind the head
    vFade = (1.0 - smoothstep(uLen - ${FADE_CELLS}.0, uLen, back)) * step(-0.01, back);
    vec2 dir = c - a; float L = length(dir); dir = L > 1e-4 ? dir / L : vec2(1.0, 0.0);
    vec3 w;
    ${flat
      ? `vec2 n = vec2(-dir.y, dir.x);
    w = vec3(p.x + n.x * position.y * 0.6, 0.003, p.y + n.y * position.y * 0.6);`
      : `w = vec3(p.x, position.y * ${(WALL_H * EDGE_OVER).toFixed(3)}, p.y);`}
    if (uBreak > 0.0) {
      // the drop: the segment flies off and falls, fading over 2 beats
      float s = aBike * 64.0 + aSeg;
      vec3 v = vec3(h11(s) - 0.5, 0.6 + h11(s * 1.7), h11(s * 2.3) - 0.5) * 6.0;
      float t = uBreak * 1.2;
      w += v * t + vec3(0.0, -9.0 * t * t, 0.0);
      vFade *= 1.0 - uBreak;
    }
    vUv = vec2(position.x, position.y * ${flat ? '1.0' : EDGE_OVER.toFixed(3)});
    vec4 cl = projectionMatrix * viewMatrix * vec4(w, 1.0);
    vNdc = cl.xy / cl.w;
    gl_Position = cl;
  }
`;
}
const WALL_FRAGMENT = `
  precision highp float;
  uniform vec3 uCol[6];
  uniform float uPulse;
  uniform float uGain;
  varying vec2 vUv;
  varying float vFade;
  varying float vBike;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  void main() {
    if (vFade <= 0.001) discard;
    int b = int(vBike + 0.5);
    vec3 col = uCol[0];
    for (int k = 1; k < 6; k++) if (k == b) col = uCol[k];
    // R1: the top edge, by its distance in pixels
    float d = abs(1.0 - vUv.y) / max(fwidth(vUv.y), 1e-5);
    float core = 1.0 - smoothstep(1.0, 2.0, d);                    // 0-2 px
    float sheath = 1.0 - smoothstep(4.5, 5.5, d);                  // to ~5 px
    // the face: almost transparent, 18 % of the colour, lighter toward the edge
    vec3 c = col * 0.18 * (0.25 + 0.75 * vUv.y) * step(vUv.y, 1.0);
    c = mix(c, col * 0.7, sheath);
    // the core: warm white tinted 30 % toward the bike (a wall is ~8 px tall
    // in the wide shots: pure white made every wall read white), just past
    // the 0.78 bloom threshold: more blooms into puffs and a grey veil
    c = mix(c, mix(vec3(0.86, 0.84, 0.80), col, 0.30), core);
    // the foot meets the floor softly
    c *= smoothstep(0.0, 0.08, vUv.y);
    gl_FragColor = vec4(c * vFade * uPulse * uGain * safeAtt(vNdc), 1.0);
  }
`;
const GLOW_FRAGMENT = `
  precision highp float;
  uniform vec3 uCol[6];
  uniform float uGain;
  varying vec2 vUv;
  varying float vFade;
  varying float vBike;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  void main() {
    if (vFade <= 0.001) discard;
    int b = int(vBike + 0.5);
    vec3 col = uCol[0];
    for (int k = 1; k < 6; k++) if (k == b) col = uCol[k];
    float a = abs(vUv.y);
    // 0.5 cell on each side (the quad is 0.6), zero before its edge (R12)
    float g = exp(-a / 0.3) * (1.0 - smoothstep(0.6, 1.0, a));
    gl_FragColor = vec4(col * 0.20 * g * vFade * uGain * safeAtt(vNdc), 1.0);
  }
`;

// the bikes: local frame x forward, y up, z to the left; lean about x, then
// heading about y (both rigid: the normals follow with the same rotations)
const BIKE_VERTEX = `
  attribute float aBike;
  attribute float aPart;          // 0 body, 1 wheel
  uniform vec4 uBike[6];          // x, z, heading (radians), visible
  uniform float uLean[6];
  varying vec3 vNv;               // view-space normal (the matcap)
  varying vec3 vL;                // local position
  varying float vPart;
  varying float vBike;
  varying vec2 vNdc;
  void main() {
    int b = int(aBike + 0.5);
    vec4 k = uBike[0]; float lean = uLean[0];
    for (int j = 1; j < 6; j++) if (j == b) { k = uBike[j]; lean = uLean[j]; }
    vBike = aBike; vPart = aPart; vL = position;
    if (k.w < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float cl = cos(lean), sl = sin(lean);
    vec3 p = vec3(position.x, position.y * cl - position.z * sl, position.z * cl + position.y * sl);
    vec3 nl = vec3(normal.x, normal.y * cl - normal.z * sl, normal.z * cl + normal.y * sl);
    float c = cos(k.z), s = sin(k.z);
    vec3 w = vec3(c * p.x - s * p.z + k.x, p.y, s * p.x + c * p.z + k.y);
    vec3 nw = vec3(c * nl.x - s * nl.z, nl.y, s * nl.x + c * nl.z);
    vNv = normalize(mat3(viewMatrix) * nw);
    vec4 cp = projectionMatrix * viewMatrix * vec4(w, 1.0);
    vNdc = cp.xy / cp.w;
    gl_Position = cp;
  }
`;
const BIKE_FRAGMENT = `
  precision highp float;
  uniform vec3 uCol[6];
  uniform float uGain;
  varying vec3 vNv;
  varying vec3 vL;
  varying float vPart;
  varying float vBike;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  void main() {
    int b = int(vBike + 0.5);
    vec3 col = uCol[0];
    for (int k = 1; k < 6; k++) if (k == b) col = uCol[k];
    vec3 n = normalize(vNv);
    vec3 c;
    if (vPart > 0.5) {
      // a wheel: a ring of light, white where the tube faces us
      c = mix(col * 1.0, vec3(0.86, 0.84, 0.80), smoothstep(0.80, 0.97, abs(n.z)));
    } else {
      // black lacquer (R5): dark sphere, two broad soft highlights below the
      // bloom threshold, a Fresnel rim of the bike's colour
      // black LACQUER: most of the sphere stays near black, the highlights are
      // soft but kept to the top of the matcap, dim enough to stay a reflection
      float h1 = smoothstep(0.78, 0.97, dot(n, normalize(vec3(-0.35, 0.75, 0.55))));
      float h2 = smoothstep(0.88, 0.99, dot(n, normalize(vec3(0.65, 0.25, 0.70))));
      float fr = pow(1.0 - abs(n.z), 3.0);
      c = vec3(0.008, 0.009, 0.012) + vec3(0.30, 0.31, 0.34) * h1 + vec3(0.14) * h2 + col * 0.60 * fr;
      // the side edge: the body's widest line, by its distance in pixels (R1)
      float d = abs(vL.y - ${BODY_Y.toFixed(3)}) / max(fwidth(vL.y), 1e-5);
      c = mix(c, col * 0.75, 1.0 - smoothstep(2.5, 3.5, d));
      c = mix(c, vec3(0.84, 0.82, 0.78), 1.0 - smoothstep(0.5, 1.5, d));
    }
    gl_FragColor = vec4(c * uGain * safeAtt(vNdc), 1.0);
  }
`;
// the light on the floor: a soft glow under the bike and the headlight's
// cone ahead of it; the quad is local x -1..3, z -1.2..1.2
const HALO_VERTEX = `
  attribute float aBike;
  uniform vec4 uBike[6];
  varying vec2 vUv;
  varying float vBike;
  varying vec2 vNdc;
  void main() {
    int b = int(aBike + 0.5);
    vec4 k = uBike[0];
    for (int j = 1; j < 6; j++) if (j == b) k = uBike[j];
    vBike = aBike; vUv = position.xy;
    if (k.w < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float c = cos(k.z), s = sin(k.z);
    vec2 l = position.xy;
    vec3 w = vec3(c * l.x - s * l.y + k.x, 0.004, s * l.x + c * l.y + k.y);
    vec4 cl = projectionMatrix * viewMatrix * vec4(w, 1.0);
    vNdc = cl.xy / cl.w;
    gl_Position = cl;
  }
`;
const HALO_FRAGMENT = `
  precision highp float;
  uniform vec3 uCol[6];
  uniform float uGain;
  varying vec2 vUv;
  varying float vBike;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  void main() {
    int b = int(vBike + 0.5);
    vec3 col = uCol[0];
    for (int k = 1; k < 6; k++) if (k == b) col = uCol[k];
    // under the bike
    float r = length(vUv * vec2(0.8, 1.6));
    float under = exp(-r / 0.3) * (1.0 - smoothstep(0.55, 0.9, r)) * 0.5;
    // the headlight: a cone from the nose, zero before every edge of the quad (R12)
    float along = vUv.x - 0.75;
    float ang = abs(vUv.y) / (along * 0.35 + 0.12);
    float cone = (1.0 - smoothstep(0.55, 1.0, ang)) * smoothstep(0.0, 0.25, along)
               * exp(-along / 1.1) * (1.0 - smoothstep(1.4, 2.1, along));
    gl_FragColor = vec4(col * (0.55 * cone + 0.30 * under) * uGain * safeAtt(vNdc), 1.0);
  }
`;

// sparks: 1-2 px points, ballistic, from up to 8 bursts
const SPARK_VERTEX = `
  attribute vec3 aSeed;
  attribute float aBurst;
  uniform vec4 uBurst[${BURSTS}];     // x, y, z, start time
  uniform vec4 uBurstK[${BURSTS}];    // r, g, b, size (speed scale; 0 = off)
  uniform float uTime;
  varying vec3 vCol;
  varying float vA;
  varying vec2 vNdc;
  void main() {
    int b = int(aBurst + 0.5);
    vec4 o = uBurst[0], k = uBurstK[0];
    for (int j = 1; j < ${BURSTS}; j++) if (j == b) { o = uBurst[j]; k = uBurstK[j]; }
    float age = uTime - o.w;
    vA = 0.0;
    if (k.w <= 0.0 || age < 0.0 || age > 1.6 || aSeed.z > k.w) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 1.0; return; }
    float th = aSeed.x * 6.2832, up = 0.25 + 0.75 * aSeed.y;
    float sp = (3.0 + 11.0 * fract(aSeed.x * 7.3 + aSeed.y * 3.1)) * (0.4 + 0.6 * k.w);
    vec3 v = vec3(cos(th) * sqrt(1.0 - up * up), up, sin(th) * sqrt(1.0 - up * up)) * sp;
    vec3 p = o.xyz + v * age + vec3(0.0, -9.8 * age * age, 0.0);
    p.y = max(p.y, 0.0);                                 // they skid on the floor
    vCol = mix(vec3(1.2, 1.15, 1.0), k.rgb, smoothstep(0.0, 0.5, age));
    vA = 1.0 - smoothstep(0.6, 1.6, age);
    vec4 cl = projectionMatrix * viewMatrix * vec4(p, 1.0);
    vNdc = cl.xy / cl.w;
    gl_Position = cl;
    gl_PointSize = 1.0;                                 // R7
  }
`;
const SPARK_FRAGMENT = `
  precision highp float;
  uniform float uGain;
  varying vec3 vCol;
  varying float vA;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  void main() {
    if (vA <= 0.0) discard;
    gl_FragColor = vec4(vCol * vA * uGain * safeAtt(vNdc), 1.0);
  }
`;

// ----------------------------------------------------------------- geometry

// a light cycle, 1.5 cells long, nose at +x: a low drop-shaped body (a
// lathe about the forward axis, round tail, long tapering nose) between two
// ring wheels; non-indexed so the two parts concatenate, aPart tells them apart
function bikeGeometry() {
  const parts = [];
  // the body
  const NU = 18, NV = 16, X0 = -0.62, X1 = 0.75, RY = 0.21, RZ = 0.15;
  const prof = (u) => (u < 0.28 ? Math.sqrt(Math.max(0, 1 - Math.pow((0.28 - u) / 0.28, 2)))
                                : Math.pow(Math.max(0, 1 - Math.pow((u - 0.28) / 0.72, 1.7)), 0.85));
  const pos = [], idx = [];
  for (let i = 0; i <= NU; i++) {
    const u = i / NU, x = X0 + (X1 - X0) * u, r = prof(u);
    for (let j = 0; j <= NV; j++) {
      const th = j / NV * Math.PI * 2;
      pos.push(x, BODY_Y + Math.cos(th) * RY * r, Math.sin(th) * RZ * r);
    }
  }
  for (let i = 0; i < NU; i++) for (let j = 0; j < NV; j++) {
    const a = i * (NV + 1) + j, b = a + NV + 1;
    // wound so the normals point OUT ((a+1)-a) x (b-a) = z x x = +y on top)
    idx.push(a, a + 1, b, a + 1, b + 1, b);
  }
  const body = new THREE.BufferGeometry();
  body.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  body.setIndex(idx);
  body.computeVertexNormals();
  parts.push([body.toNonIndexed(), 0]);
  body.dispose();
  // the wheels: rings in the bike's plane (TorusGeometry lies in xy, axis z)
  for (const wx of [0.50, -0.50]) {
    const t = new THREE.TorusGeometry(0.24, 0.05, 8, 28);
    t.translate(wx, 0.28, 0);
    parts.push([t.toNonIndexed(), 1]);
    t.dispose();
  }
  let n = 0; for (const [g] of parts) n += g.attributes.position.count;
  const P = new Float32Array(n * 3), Nn = new Float32Array(n * 3), A = new Float32Array(n);
  let o = 0;
  for (const [g, part] of parts) {
    const c = g.attributes.position.count;
    P.set(g.attributes.position.array, o * 3); Nn.set(g.attributes.normal.array, o * 3); A.fill(part, o, o + c);
    o += c; g.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(P, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(Nn, 3));
  g.setAttribute('aPart', new THREE.BufferAttribute(A, 1));
  return g;
}
function instanced(base, counts, attrs) {
  const g = new THREE.InstancedBufferGeometry();
  if (base.index) g.index = base.index;
  for (const n of Object.keys(base.attributes)) g.setAttribute(n, base.getAttribute(n));
  for (const [name, arr] of Object.entries(attrs)) g.setAttribute(name, new THREE.InstancedBufferAttribute(arr, 1));
  g.instanceCount = counts;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
  return g;
}

// =============================================================================

export class LightcyclesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    const n = parseInt(urlParam('lccount'), 10);
    this._n = Number.isFinite(n) ? Math.max(2, Math.min(6, n)) : this._layout.bikes;
    const v = urlParam('lcview');
    this._pinView = PIN_VIEWS.includes(v) ? v : null;
    this._crashDebug = urlParam('lccrash') === '1';
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0.015, vignette: 0.35 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('lcprobe') === '1' && typeof window !== 'undefined') window.__lightcycles = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._group = new THREE.Group(); scene.add(this._group);
    const safe = { uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._gain = { value: 1 };
    const N = this._n, SEG = this._layout.segs;
    this._cols = Array.from({ length: 6 }, () => new THREE.Color(1, 1, 1));
    this._segData = new Float32Array(MAX_SEGS * 12 * 4);
    this._segTex = new THREE.DataTexture(this._segData, MAX_SEGS, 12, THREE.RGBAFormat, THREE.FloatType);
    this._segTex.minFilter = this._segTex.magFilter = THREE.NearestFilter; this._segTex.needsUpdate = true;
    const U = this._U = Object.assign({
      uSegs: { value: this._segTex }, uSegsSize: { value: new THREE.Vector2(MAX_SEGS, 12) },
      uHead: { value: Array.from({ length: 6 }, () => new THREE.Vector4()) }, uHeadOdo: { value: new Array(6).fill(0) },
      uLen: { value: WALL_LEN }, uBreak: { value: 0 }, uCol: { value: this._cols }, uPulse: { value: 1 }, uGain: this._gain,
      uBike: { value: Array.from({ length: 6 }, () => new THREE.Vector4()) }, uLean: { value: new Array(6).fill(0) }, uTime: { value: 0 },
      uBurst: { value: Array.from({ length: BURSTS }, () => new THREE.Vector4()) }, uBurstK: { value: Array.from({ length: BURSTS }, () => new THREE.Vector4()) },
      uGridCol: { value: new THREE.Color(0.5, 0.75, 1.0) }
    }, safe);
    const add = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending };
    // the grid
    this._gridGeo = new THREE.PlaneGeometry(600, 600); this._gridGeo.rotateX(-Math.PI / 2);
    this._gridMat = new THREE.ShaderMaterial({ uniforms: Object.assign({ uCol: U.uGridCol, uGain: this._gain }, safe), vertexShader: GRID_VERTEX, fragmentShader: GRID_FRAGMENT, depthWrite: true });
    this._grid = new THREE.Mesh(this._gridGeo, this._gridMat); this._grid.frustumCulled = false; this._grid.renderOrder = -10;
    // the walls and their glow: (bikes x segments) instances
    const aBike = new Float32Array(N * SEG), aSeg = new Float32Array(N * SEG);
    for (let b = 0; b < N; b++) for (let j = 0; j < SEG; j++) { aBike[b * SEG + j] = b; aSeg[b * SEG + j] = j; }
    const ribbon = new THREE.PlaneGeometry(1, 1, 1, 1); ribbon.translate(0.5, 0.5, 0);
    const flat = new THREE.PlaneGeometry(1, 2, 1, 1); flat.translate(0.5, 0, 0);
    this._bases = [ribbon, flat];
    this._wallGeo = instanced(ribbon, N * SEG, { aBike, aSeg });
    this._glowGeo = instanced(flat, N * SEG, { aBike: aBike.slice(), aSeg: aSeg.slice() });
    this._wallMat = new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: wallVertex(false), fragmentShader: WALL_FRAGMENT, side: THREE.DoubleSide }, add));
    this._glowMat = new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: wallVertex(true), fragmentShader: GLOW_FRAGMENT, side: THREE.DoubleSide, depthTest: false }, add));
    this._walls = new THREE.Mesh(this._wallGeo, this._wallMat); this._walls.frustumCulled = false; this._walls.renderOrder = 2;
    this._glow = new THREE.Mesh(this._glowGeo, this._glowMat); this._glow.frustumCulled = false; this._glow.renderOrder = 1;
    // the bikes and their halos
    const ab = new Float32Array(N); for (let b = 0; b < N; b++) ab[b] = b;
    this._bikeBase = bikeGeometry();
    this._bikeGeo = instanced(this._bikeBase, N, { aBike: ab });
    this._bikeMat = new THREE.ShaderMaterial({ uniforms: U, vertexShader: BIKE_VERTEX, fragmentShader: BIKE_FRAGMENT, side: THREE.DoubleSide });
    this._bikes = new THREE.Mesh(this._bikeGeo, this._bikeMat); this._bikes.frustumCulled = false; this._bikes.renderOrder = 3;
    const haloBase = new THREE.PlaneGeometry(4, 2.4); haloBase.translate(1, 0, 0); this._bases.push(haloBase);
    this._haloGeo = instanced(haloBase, N, { aBike: ab.slice() });
    // DoubleSide: the quad's (x, y) map to world (x, z), so its normal points DOWN
    // and single-sided it is culled from every camera above the floor
    this._haloMat = new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: HALO_VERTEX, fragmentShader: HALO_FRAGMENT, depthTest: false, side: THREE.DoubleSide }, add));
    this._halo = new THREE.Mesh(this._haloGeo, this._haloMat); this._halo.frustumCulled = false; this._halo.renderOrder = 1;
    // the sparks
    const r = rng(77), seeds = new Float32Array(SPARKS * 3), bursts = new Float32Array(SPARKS);
    for (let i = 0; i < SPARKS; i++) { seeds[i * 3] = r(); seeds[i * 3 + 1] = r(); seeds[i * 3 + 2] = r(); bursts[i] = i % BURSTS; }
    this._sparkGeo = new THREE.BufferGeometry();
    this._sparkGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(SPARKS * 3), 3));
    this._sparkGeo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 3));
    this._sparkGeo.setAttribute('aBurst', new THREE.BufferAttribute(bursts, 1));
    this._sparkMat = new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: SPARK_VERTEX, fragmentShader: SPARK_FRAGMENT, depthTest: false }, add));
    this._sparks = new THREE.Points(this._sparkGeo, this._sparkMat); this._sparks.frustumCulled = false; this._sparks.renderOrder = 4;
    this._group.add(this._grid, this._glow, this._halo, this._walls, this._bikes, this._sparks);

    // state
    this._rnd = rng(1983);
    this._time = 0; this._beats = 0; this._bars = 0; this._phrases = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0;
    this._secondsBeat = 0.5; this._buildup = 0; this._sub = 2; this._done = 0; this._needPlan = true;
    this._bursts = []; this._burstSlot = 0;
    this._viewIdx = 0; this._camKick = 0;
    this._cam = { target: new THREE.Vector3(), pos: new THREE.Vector3(0, 30, 25), init: false };
    this._fitCache = {}; this._lowUntil = -1;
    this._drop = null; this._lastDropAt = -1e9;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._paletteKey = null; this._hueBase = 0.85;
    this._colFrom = []; this._colTo = []; this._colT0 = new Array(6).fill(-1e9);
    this._stats = { steps: 0, turns: 0, offBeatTurns: 0, violations: 0, crashes: 0, drops: 0, minMoving: 99 };
    this._applyPalette(null);
    this._startStar(0);
    this._writeSegs();
  }

  // ------------------------------------------------------------ the world

  // occupancy on a typed grid over the arena: wall counts, this beat's
  // reservations (stamped with a generation), a visited stamp for the search
  _gridReset() {
    const n = GW * GH;
    this._occ = new Uint8Array(n);
    if (!this._res) { this._res = new Uint32Array(n); this._seen = new Uint32Array(n); this._queue = new Int32Array(n); this._gen = 1; this._seenGen = 1; }
  }
  _idx(x, z) { return (Math.abs(x) > ARENA.x || Math.abs(z) > ARENA.z) ? -1 : (x + ARENA.x) + (z + ARENA.z) * GW; }
  _occupied(x, z) { const i = this._idx(x, z); return i < 0 || this._occ[i] > 0; }
  _blocked(x, z) { const i = this._idx(x, z); return i < 0 || this._occ[i] > 0 || this._res[i] === this._gen; }
  _occAdd(x, z) { const i = this._idx(x, z); if (i >= 0) this._occ[i]++; }
  _occRemove(x, z) { const i = this._idx(x, z); if (i >= 0 && this._occ[i] > 0) this._occ[i]--; }

  // the bikes leave the centre in a star, on an empty grid
  _startStar(t) {
    this._gridReset();
    this._bikesS = [];
    for (let b = 0; b < this._n; b++) {
      const d = b % 4, ring = Math.floor(b / 4);
      const side = DIRS[(d + 1) % 4];
      const x = DIRS[d][0] * (1 + ring) + side[0] * ring * 2, z = DIRS[d][1] * (1 + ring) + side[1] * ring * 2;
      const bk = { x, z, dir: d, cells: [{ x, z, odo: 0 }], odo: 0, plan: [], from: { x, z }, alive: true, turnT: -1e9, turnFrom: 0, turnDelta: 0, turnDur: 1 };
      this._occAdd(x, z);
      this._bikesS.push(bk);
    }
    this._planT = t;
  }

  // free cells reachable from (x, z) within depthMax steps (breadth-first),
  // walls and this beat's reservations counted as blocked
  _space(x, z, depthMax) {
    const W = GW, seen = this._seen, q = this._queue, sg = ++this._seenGen;
    const i0 = this._idx(x, z); if (i0 < 0) return 0;
    seen[i0] = sg; q[0] = i0;
    let head = 0, tail = 1, n = 0, levelEnd = 1;
    for (let depth = 0; depth < depthMax && head < tail; depth++) {
      for (; head < levelEnd; head++) {
        const i = q[head], cx = i % W - ARENA.x, cz = ((i / W) | 0) - ARENA.z;
        for (let d = 0; d < 4; d++) {
          const nx = cx + DIRS[d][0], nz = cz + DIRS[d][1];
          const j = this._idx(nx, nz);
          if (j < 0 || seen[j] === sg || this._occ[j] || this._res[j] === this._gen) continue;
          seen[j] = sg; q[tail++] = j; n++;
        }
      }
      levelEnd = tail;
    }
    return n;
  }

  // the `steps` straight cells from (x, z) heading d, or null if any is blocked
  _run(x, z, d, steps) {
    const cells = [];
    for (let s = 0; s < steps; s++) { x += DIRS[d][0]; z += DIRS[d][1]; if (this._blocked(x, z)) return null; cells.push({ x, z }); }
    return cells;
  }
  _mark(cells, v) { for (const c of cells) this._res[this._idx(c.x, c.z)] = v; }

  // score of ending a beat at the end of `cells` heading d: the brief's free
  // space within 8 cells, taken from the BEST move of the following beat (a
  // two-beat look-ahead: a choice with no way out next beat ranks last), and
  // a pocket test (fewer than LOOK_CAP cells reachable at depth 40 ranks
  // below every open option)
  _score(cells, d, steps) {
    const g = this._gen;
    this._mark(cells, g);
    const last = cells[cells.length - 1];
    let best = -2000;
    for (const d2 of [d, (d + 1) % 4, (d + 3) % 4]) {
      const c2 = this._run(last.x, last.z, d2, steps);
      if (!c2) continue;
      this._mark(c2, g);
      const e = c2[c2.length - 1];
      let sc = this._space(e.x, e.z, LOOK);
      if (this._space(e.x, e.z, 40) < LOOK_CAP) sc -= 1000;
      this._mark(c2, 0);
      if (sc > best) best = sc;
    }
    this._mark(cells, 0);
    return best;
  }

  // THE BEAT'S DECISIONS: each bike plans its `steps` cells for this beat
  _decide(steps, strong) {
    this._gen++;
    const g = this._gen;
    for (const bk of this._bikesS) if (bk.alive) { const i = this._idx(bk.x, bk.z); if (i >= 0) this._res[i] = g; }
    // the planning order rotates every beat: no bike always has the right of way
    const N = this._bikesS.length, first = this._beats % N;
    for (let k = 0; k < N; k++) {
      const bk = this._bikesS[(first + k) % N];
      if (bk.alive) this._decideBike(bk, steps, strong);
    }
  }
  _decideBike(bk, steps, strong) {
    const valid = [];
    [bk.dir, (bk.dir + 1) % 4, (bk.dir + 3) % 4].forEach((d, i) => {
      const cells = this._run(bk.x, bk.z, d, steps);
      if (cells) valid.push({ d, cells, score: this._score(cells, d, steps), turn: i > 0, r: this._rnd() });
    });
    if (!valid.length) { bk.plan = []; bk.stuck = true; return; }
    const best = Math.max(...valid.map((o) => o.score));
    // the most space wins; among (near) ties, straight unless it is a strong beat
    const good = valid.filter((o) => (best >= 0 ? o.score >= best * 0.8 : o.score === best));
    good.sort((a, b) => (strong ? (b.turn - a.turn) : (a.turn - b.turn)) || (b.score - a.score) || (a.r - b.r));
    const pick = good[0];
    if (pick.d !== bk.dir) {
      // the quarter turn is drawn over the first step: pivot and lean
      this._stats.turns++;
      bk.turnFrom = bk.dir * Math.PI / 2;
      bk.turnDelta = (pick.d - bk.dir + 4) % 4 === 1 ? Math.PI / 2 : -Math.PI / 2;
      bk.turnT = this._time; bk.turnDur = this._secondsBeat / steps;
    }
    bk.dir = pick.d; bk.plan = pick.cells.slice();
    this._mark(pick.cells, this._gen);
  }

  // one step: every bike moves one cell along its plan
  _step(t) {
    const len = this._buildup > 0.5 ? WALL_LEN_BUILD : WALL_LEN;
    for (const bk of this._bikesS) if (bk.alive && bk.plan.length) this._stepBike(bk, len);
    this._stats.steps++;
  }
  _stepBike(bk, len) {
    len = len || (this._buildup > 0.5 ? WALL_LEN_BUILD : WALL_LEN);
    const c = bk.plan.shift();
    if (this._occupied(c.x, c.z)) this._stats.violations++;      // criterion 3: never happens
    bk.x = c.x; bk.z = c.z; bk.odo++;
    bk.cells.push({ x: c.x, z: c.z, odo: bk.odo }); this._occAdd(c.x, c.z);
    while (bk.cells.length > len) { const o = bk.cells.shift(); this._occRemove(o.x, o.z); }
    this._segSig = -1;
  }

  // a stuck bike crashes alone and comes back at the edge of the zone
  _crashAlone(bk, t) {
    this._burst(bk.x, bk.z, t, this._cols[this._bikesS.indexOf(bk)], 0.35);
    for (const o of bk.cells) this._occRemove(o.x, o.z);
    this._stats.crashes++;
    // the free edge cell with the most space in front; the other bikes'
    // cells and this beat's plans count as walls (they move into them)
    this._gen++;
    for (const o of this._bikesS) if (o !== bk && o.alive) this._mark([o, ...o.plan], this._gen);
    let best = null;
    for (let tries = 0; tries < 40; tries++) {
      const side = Math.floor(this._rnd() * 4), d = (side + 2) % 4;
      const along = Math.round((this._rnd() * 2 - 1) * (side % 2 ? ARENA.x - 2 : ARENA.z - 2));
      const x = side === 0 ? ARENA.x : side === 2 ? -ARENA.x : along, z = side === 1 ? ARENA.z : side === 3 ? -ARENA.z : along;
      if (this._blocked(x, z)) continue;
      const s = this._space(x, z, 40);
      if (!best || s > best.s) best = { x, z, d, s };
    }
    if (!best) { bk.alive = false; bk.cells = []; this._segSig = -1; return; }
    Object.assign(bk, { x: best.x, z: best.z, dir: best.d, cells: [{ x: best.x, z: best.z, odo: bk.odo }], plan: [], from: { x: best.x, z: best.z }, stuck: false, turnT: -1e9 });
    this._occAdd(best.x, best.z);
    this._segSig = -1;
  }

  _burst(x, z, t, col, size) {
    this._bursts[this._burstSlot] = { x, y: 0.3, z, t, col: col.clone(), size };
    this._burstSlot = (this._burstSlot + 1) % BURSTS;
  }

  // the walls as segments: each straight run of the cell queue
  _writeSegs() {
    const D = this._segData; D.fill(0);
    const SEG = this._layout.segs;
    this._bikesS.forEach((bk, b) => {
      const cells = bk.cells; if (!cells.length) return;
      const segs = [];
      let s0 = 0;
      for (let i = 1; i < cells.length; i++) {
        const dx = cells[i].x - cells[i - 1].x, dz = cells[i].z - cells[i - 1].z;
        const pdx = i > 1 ? cells[i - 1].x - cells[i - 2].x : dx, pdz = i > 1 ? cells[i - 1].z - cells[i - 2].z : dz;
        if (i > 1 && (dx !== pdx || dz !== pdz)) { segs.push([s0, i - 1]); s0 = i - 1; }
      }
      if (cells.length > 1) segs.push([s0, cells.length - 1]);
      // the last slot is the live segment: from the bike's cell to its head
      // (the uniform), so a fresh turn never stretches the previous run
      const keep = segs.slice(-(SEG - 1));
      keep.push([cells.length - 1, cells.length - 1, true]);
      keep.forEach(([i0, i1, live], j) => {
        const a = cells[i0], c = cells[i1];
        const o = (b * 2 * MAX_SEGS + j) * 4, o2 = ((b * 2 + 1) * MAX_SEGS + j) * 4;
        D[o] = a.x; D[o + 1] = a.z; D[o + 2] = c.x; D[o + 3] = c.z;
        D[o2] = a.odo; D[o2 + 1] = c.odo; D[o2 + 2] = live ? 2 : 1;
      });
    });
    this._segTex.needsUpdate = true;
  }

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : FALLBACK;
    const k = v.map(String).join('|');
    if (k === this._paletteKey) return;
    this._paletteKey = k;
    const c = vdjColor(v[0]), hsl = { h: 0, s: 0, l: 0 }; c.getHSL(hsl);
    this._hueBase = hsl.h;
    // R4: hues 1/n of the turn apart from the cover's accent
    this._hues = Array.from({ length: this._n }, (_, b) => (this._hueBase + b / this._n) % 1);
    for (let b = 0; b < this._n; b++) this._cols[b].copy(this._bikeColour(b));
  }
  _bikeColour(b) { return new THREE.Color().setHSL(this._hues[b], 1.0, 0.5); }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  // A FIXED SHOT THAT HOLDS THE WHOLE ARENA (R6, R19): distance d and a
  // slide s of the look-at point along the view's ground direction, solved
  // so that the eight corners of the arena (walls included) fill FRAME,
  // centred on the screen. Cached per view and aspect.
  _fit(view, aspect) {
    const key = view + ':' + aspect.toFixed(3);
    if (this._fitCache[key]) return this._fitCache[key];
    const cam = new THREE.PerspectiveCamera(FOV, aspect, 0.1, 1000);
    const top = view === 'top';
    const pitch = THREE.MathUtils.degToRad(top ? 90 : PITCH);
    const az = view === 'high2' ? Math.PI : 0;
    const g = new THREE.Vector3(-Math.sin(az), 0, -Math.cos(az));     // where the camera looks, on the ground
    const corners = [];
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) for (const y of [0, WALL_H * EDGE_OVER]) corners.push(new THREE.Vector3(sx * BORDER.x, y, sz * BORDER.z));
    const target = new THREE.Vector3(), q = new THREE.Vector3();
    const place = (d, sl) => {
      target.copy(g).multiplyScalar(sl);
      cam.position.copy(target).addScaledVector(g, -d * Math.cos(pitch)); cam.position.y = d * Math.sin(pitch);
      if (top) cam.up.copy(g); else cam.up.set(0, 1, 0);
      cam.lookAt(target); cam.updateMatrixWorld(); cam.updateProjectionMatrix();
    };
    const box = () => {
      const r = { x0: 9, x1: -9, y0: 9, y1: -9 };
      for (const c of corners) { q.copy(c).project(cam); r.x0 = Math.min(r.x0, q.x); r.x1 = Math.max(r.x1, q.x); r.y0 = Math.min(r.y0, q.y); r.y1 = Math.max(r.y1, q.y); }
      return r;
    };
    const want = (FRAME.yt + FRAME.yb) / 2;
    let d = 60, sl = 0;
    for (let it = 0; it < 40; it++) {
      place(d, sl); let r = box();
      const scale = Math.max((r.y1 - r.y0) / (FRAME.yt - FRAME.yb), (r.x1 - r.x0) / (2 * FRAME.x));
      d *= Math.pow(scale, 0.8);
      place(d, sl); r = box();
      const cy = (r.y0 + r.y1) / 2 - want, eps = 0.01 * d;
      place(d, sl + eps); const r2 = box();
      const dcy = ((r2.y0 + r2.y1) / 2 - (r.y0 + r.y1) / 2) / eps;
      if (Math.abs(dcy) > 1e-6) sl -= cy / dcy;
    }
    place(d, sl);
    const out = { pos: cam.position.clone(), target: target.clone(), up: cam.up.clone(), box: box() };
    this._fitCache[key] = out;
    return out;
  }

  _view() {
    if (this._pinView) return this._pinView;
    if (this._beats < this._lowUntil) return 'low';
    return VIEWS[this._viewIdx % VIEWS.length];
  }

  _placeCamera(dt) {
    const view = this._view();
    const cam = this._camera, s = this._cam;
    // the camera's own aspect: it is what projects (the safe-zone uniform can lag a resize)
    const aspect = cam.aspect || 16 / 9;
    cam.fov = FOV; cam.near = 0.1; cam.far = 600;
    if (view === 'low') {
      // the drop's shot: low, behind bike 0, looking toward the centre of the arena
      const p = (this._renderPos || [])[0] || { x: 0, z: 0, a: 0 };
      _v2.set(p.x - Math.cos(p.a) * 6, 1.6, p.z - Math.sin(p.a) * 6);
      _v3.set(p.x + Math.cos(p.a) * 6, 0, p.z + Math.sin(p.a) * 6);
      const k = s.init && s.view === 'low' ? 1 - Math.exp(-dt / 0.25) : 1;
      s.pos.lerp(_v2, k); s.target.lerp(_v3, k); s.init = true; s.view = 'low';
      cam.up.set(0, 1, 0);
      cam.position.copy(s.pos);
      cam.lookAt(s.target);
    } else {
      const f = this._fit(view, aspect);
      s.view = view;
      cam.up.copy(f.up);
      // the bar's kick: a short dip toward the floor, inside the 5 % margins
      cam.position.copy(f.pos).addScaledVector(_v1.copy(f.target).sub(f.pos).normalize(), this._camKick * 0.6);
      cam.lookAt(f.target);
    }
    cam.updateProjectionMatrix(); cam.updateMatrixWorld();
  }

  update(deltaTime, profile) {
    this._applyPalette(profile.artworkPalette);
    if (profile.playbackState !== 'PLAYING') { this._placeCamera(0); this._post.update(1); return; }   // R11: a freeze
    const dt = Math.min(deltaTime, 0.1);
    const t = this._time += dt;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / THREE.MathUtils.clamp(bpm, 60, 200) : 0.5;          // without a BPM: 120
    const beatS = this._secondsBeat;
    let bp = profile.beatPhase;
    if (bp == null) { this._free = (this._free + dt / beatS) % 1; bp = this._free; }
    let beat = false, bar = false, phrase = false;
    if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true;
    this._phaseBeat = bp;
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (bar) this._bars++;

    // MONTÉE
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 0.8));
    const sub = this._buildup > 0.5 ? 4 : 2;          // steps per beat
    this._U.uLen.value = this._buildup > 0.5 ? WALL_LEN_BUILD : WALL_LEN;

    // DROP
    let drop = this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (this._crashDebug && beat && this._beats % 16 === 8) drop = true;
    if (drop && t - this._lastDropAt > 4 * beatS && !this._drop) {
      this._lastDropAt = t; this._stats.drops++;
      // the bikes rush to the centre (one beat), crash, walls shatter (2 beats)
      this._drop = { at: t, c: new THREE.Vector3(0, 0, 0), from: (this._renderPos || []).map((p) => ({ x: p.x, z: p.z, a: p.a })), crashed: false };
      this._lowUntil = this._beats + LOW_BEATS;
    }
    if (this._drop) {
      const u = (t - this._drop.at) / beatS;
      if (u >= 1 && !this._drop.crashed) {
        this._drop.crashed = true;
        // every slot: one spray per bike, the rest white-hot from the impact
        for (let k = 0; k < BURSTS; k++) {
          const a = k / BURSTS * Math.PI * 2;
          this._burst(this._drop.c.x + Math.cos(a) * 0.4, this._drop.c.z + Math.sin(a) * 0.4, t, k < this._n ? this._cols[k] : _white, 1.0);
        }
        this._flash = this._comfort.requestFlash(t) ? 1 : 0.3;
      }
      this._U.uBreak.value = u < 1 ? 0 : clamp01((u - 1) / 2);
      if (u >= 3) {
        // a fresh start in a star from the centre, on an empty grid
        this._U.uBreak.value = 0;
        this._startStar(t);
        this._drop = null; this._needPlan = true;
        this._writeSegs();
      }
    }

    // THE STEPS: on the eighths (sixteenths in a build-up); the decisions on the beat
    if (!this._drop) {
      // a beat edge first completes the previous beat's plan (its last step
      // lands exactly on the beat), then decides; within the beat the k-th
      // step fires when beatPhase crosses k / sub
      if (beat) for (const bk of this._bikesS) while (bk.plan.length) this._stepBike(bk);
      if (beat || this._needPlan) {
        const strong = barPhase != null ? barPhase < 0.25 / 4 + 0.02 : this._beats % 4 === 0;
        this._decide(sub, strong);
        // a stuck bike crashes, comes back at the edge AND sets off in the
        // same beat: six bikes always in play
        for (const bk of this._bikesS) if (bk.stuck) { this._crashAlone(bk, t); if (bk.alive) { bk.stuck = false; this._decideBike(bk, sub, strong); if (bk.stuck) { this._crashAlone(bk, t); bk.stuck = false; } } }
        let moving = 0; for (const bk of this._bikesS) if (bk.alive && bk.plan.length) moving++;
        this._stats.minMoving = Math.min(this._stats.minMoving, moving);
        this._needPlan = false;
        this._pulse = 1;
        this._sub = sub; this._done = 0;
      }
      const due = Math.min(this._sub, Math.floor(bp * this._sub));
      while (this._done < due) { this._done++; this._step(t); }
      this._writeSegsIfChanged();
    }
    this._stepFrac = clamp01((bp * (this._sub || sub)) % 1);

    // MESURE: one bike changes colour through white (swaps hues with another); a camera kick
    if (bar) {
      const b = this._bars % this._n, o = (b + 2) % this._n;
      [this._hues[b], this._hues[o]] = [this._hues[o], this._hues[b]];
      this._colT0[b] = t; this._colT0[o] = t;
      this._camKick = 1;
    }
    for (let b = 0; b < this._n; b++) {
      const target = this._bikeColour(b);
      const u = (t - this._colT0[b]) / (0.5 * beatS);
      this._cols[b].copy(target);
      if (u >= 0 && u < 1) this._cols[b].lerp(_white, 1 - Math.abs(u * 2 - 1));
    }
    // PHRASE: the next view, a cut
    if (phrase) { this._phrases++; this._viewIdx++; }
    this._camKick *= Math.exp(-dt / 0.18);
    this._pulse = (this._pulse || 0) * Math.exp(-dt / 0.12);
    this._U.uPulse.value = 1 + 0.10 * this._pulse;            // the edges pulse +10 % on the beat

    // the bikes' rendered positions (interpolated between cells; to the centre on a drop)
    this._renderPos = this._bikesS.map((bk, b) => {
      let x, z, a = bk.dir * Math.PI / 2, lean = 0;
      if (this._drop && this._drop.from[b]) {
        const f = this._drop.from[b], u = clamp01((t - this._drop.at) / beatS);
        x = f.x + (this._drop.c.x - f.x) * u * u; z = f.z + (this._drop.c.z - f.z) * u * u;
        a = Math.atan2(this._drop.c.z - f.z, this._drop.c.x - f.x);
      } else {
        const f = this._stepFrac;
        const nxt = bk.plan[0];
        if (nxt) { x = bk.x + (nxt.x - bk.x) * f; z = bk.z + (nxt.z - bk.z) * f; } else { x = bk.x; z = bk.z; }
        // the quarter turn: pivot over the first step, lean 15 degrees inward and back
        const u = (t - bk.turnT) / bk.turnDur;
        if (u >= 0 && u < 1) {
          const e = u * u * (3 - 2 * u);
          a = bk.turnFrom + bk.turnDelta * e;
          lean = Math.sign(bk.turnDelta) * LEAN * Math.sin(Math.PI * u);
        }
      }
      return { x, z, a, lean, odo: bk.odo + (bk.plan[0] && !this._drop ? this._stepFrac : 0) };
    });
    for (let b = 0; b < 6; b++) {
      const p = this._renderPos[b], u = this._U.uBike.value[b], h = this._U.uHead.value[b];
      const visible = p && this._bikesS[b].alive && !(this._drop && this._drop.crashed);
      if (!p) { u.set(0, 0, 0, 0); continue; }
      u.set(p.x, p.z, p.a, visible ? 1 : 0);
      this._U.uLean.value[b] = p.lean || 0;
      // on a drop the walls stay where the bike left them: rectilinear, no streak to the centre
      const bk = this._bikesS[b];
      if (this._drop) { h.set(bk.x, bk.z, 0, 0); this._U.uHeadOdo.value[b] = bk.odo; }
      else { h.set(p.x, p.z, 0, 0); this._U.uHeadOdo.value[b] = p.odo; }
    }
    // the sparks
    for (let i = 0; i < BURSTS; i++) {
      const s = this._bursts[i], u = this._U.uBurst.value[i], k = this._U.uBurstK.value[i];
      if (!s || t - s.t > 1.6) { k.set(0, 0, 0, 0); continue; }
      u.set(s.x, s.y, s.z, s.t); k.set(s.col.r, s.col.g, s.col.b, s.size);
    }
    this._U.uTime.value = t;
    // the grid's colour: the first bike's, faint
    this._U.uGridCol.value.copy(this._cols[0]).lerp(_white, 0.35);
    // flash (the drop), comfort
    this._flash = (this._flash || 0) * Math.exp(-dt / 0.12);
    const est = 0.04 + 0.02 * this._n + 0.25 * this._flash;
    this._gain.value = this._comfort.measure(est, dt) * (1 + 0.6 * this._flash);
    this._placeCamera(dt);
    this._post.update(vdjBeatChroma(bp, beatS));
  }

  // the arena's corners (walls included) through the live camera, in NDC
  _arenaNdc() {
    const cam = this._camera; if (!cam) return null;
    const r = { x0: 9, x1: -9, y0: 9, y1: -9 };
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) for (const y of [0, WALL_H * EDGE_OVER]) {
      _v1.set(sx * BORDER.x, y, sz * BORDER.z).project(cam);
      r.x0 = Math.min(r.x0, _v1.x); r.x1 = Math.max(r.x1, _v1.x); r.y0 = Math.min(r.y0, _v1.y); r.y1 = Math.max(r.y1, _v1.y);
    }
    for (const k of Object.keys(r)) r[k] = +r[k].toFixed(3);
    return r;
  }

  _writeSegsIfChanged() {
    if (this._segSig !== 0) { this._segSig = 0; this._writeSegs(); }
  }

  getProbe() {
    return {
      tier: this._tier, n: this._n, beats: this._beats, stats: Object.assign({}, this._stats), view: this._view(),
      arenaNdc: this._arenaNdc(),
      bikes: (this._bikesS || []).map((b) => ({ x: b.x, z: b.z, dir: b.dir, cells: b.cells.length, alive: b.alive })), drop: !!this._drop, buildup: +this._buildup.toFixed(2),
      hues: this._hues ? this._hues.map((h) => +h.toFixed(3)) : null
    };
  }

  dispose() {
    if (this._scene) { this._scene.remove(this._group); this._scene.background = this._prevBackground || null; }
    for (const g of [this._gridGeo, this._wallGeo, this._glowGeo, this._bikeGeo, this._haloGeo, this._sparkGeo, this._bikeBase, ...(this._bases || [])]) if (g) g.dispose();
    for (const m of [this._gridMat, this._wallMat, this._glowMat, this._bikeMat, this._haloMat, this._sparkMat]) if (m) m.dispose();
    if (this._segTex) this._segTex.dispose();
    if (typeof window !== 'undefined' && window.__lightcycles === this) window.__lightcycles = null;
    this._scene = null;
  }
}

const _v1 = new THREE.Vector3(), _v2 = new THREE.Vector3(), _v3 = new THREE.Vector3();
const _white = new THREE.Color(1, 1, 1);
